import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { normalizeNum } from "@/lib/format";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateOperacia,
  useDeleteOperacia,
  useOperacie,
  useUpdateOperacia,
} from "@/api/hooks/useOperacie";
import {
  KATEGORIE_OPERACII,
  jePostup,
  zoskupOperacie,
} from "@/lib/operacie";
import type { Operation } from "@/api/schemas";

/**
 * Cenník → tab Operácie: platené operácie zoskupené podľa kategórií.
 * Cena sa mení inline; plná správa (pridať, upraviť názov/MJ/réžiu/
 * kategóriu, zmazať) je tu tiež — rovnaké dialógy ako v Nastaveniach.
 */
export function OperacieCennikSection(): JSX.Element {
  const opQuery = useOperacie({ systemove: false });
  const createMutation = useCreateOperacia();
  const deleteMutation = useDeleteOperacia();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Operation | null>(null);
  const operacie = opQuery.data?.data ?? [];
  const skupiny = zoskupOperacie(operacie);

  async function handleDelete(op: Operation): Promise<void> {
    if (!window.confirm(`Zmazať operáciu ${op.label ?? op.kod}?`)) return;
    try {
      await deleteMutation.mutateAsync(op.kod);
      toast({ title: "Operácia zmazaná" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri mazaní",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl">Operácie</h2>
          <p className="text-xs text-muted-foreground">
            Ceny operácií pridávaných na dielce (m² → plocha, m → dĺžka X,
            ks/hod → za kus). Cenu zmeň priamo v poli; ceruzka upraví
            názov, kategóriu, MJ a réžiu.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Nová operácia
        </Button>
      </div>
      {opQuery.isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Načítava sa…
        </p>
      ) : (
        skupiny.map((skupina) => (
          <div key={skupina.kod ?? "nezaradene"}>
            <h3 className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {skupina.label}
            </h3>
            <div className="rounded-md border">
              <Table>
                <TableBody>
                  {skupina.items.map((op) => (
                    <TableRow key={op.kod} data-testid={`op-cennik-${op.kod}`}>
                      <TableCell className="py-1.5">
                        {op.label ?? op.kod}
                        {parseFloat(String(op.rezia_pct)) > 0 ? (
                          <span className="ml-2 text-[10px] text-muted-foreground">
                            réžia {normalizeNum(op.rezia_pct)} %
                          </span>
                        ) : null}
                        {/* Nenulová nadmiera musí byť vidieť zo zoznamu —
                            inak sa o nej nikto nedozvie (lekcia KOLA 58). */}
                        {parseFloat(String(op.nadmiera_x_mm ?? "0")) > 0 ||
                        parseFloat(String(op.nadmiera_y_mm ?? "0")) > 0 ? (
                          <span
                            className="ml-2 rounded bg-warning/15 px-1 text-[10px] text-warning"
                            title="Dielec s touto operáciou sa reže väčší; odpočet hrán sa od toho ešte odráta"
                          >
                            nadmiera +{normalizeNum(op.nadmiera_x_mm ?? "0")}/+
                            {normalizeNum(op.nadmiera_y_mm ?? "0")} mm
                          </span>
                        ) : null}
                        {/* Vypnuté odčítanie je rovnako podstatné ako
                            nadmiera — bez štítku by sa dva dielce s tou
                            istou hranou rozišli bez viditeľného dôvodu. */}
                        {op.odcitat_hrany === false ? (
                          <span
                            className="ml-2 rounded bg-background-alt px-1 text-[10px] text-muted-foreground"
                            title="Hrana sa z prírezu neodčítava — nadmiera ide k rozmeru s hranou"
                            data-testid="bez-odcitania"
                          >
                            bez odčítania hrán
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="w-[170px] py-1 text-right">
                        <CenaInline op={op} />
                      </TableCell>
                      <TableCell className="w-[76px] py-1">
                        <div className="flex justify-end gap-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setEditTarget(op)}
                            className="h-7 w-7"
                            aria-label={`Upraviť ${op.kod}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(op)}
                            className="h-7 w-7 hover:text-destructive"
                            aria-label={`Zmazať ${op.kod}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))
      )}

      <OperaciaCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={async (data) => {
          await createMutation.mutateAsync(data);
          toast({ title: "Operácia pridaná", description: data.kod });
        }}
      />
      <OperaciaEditDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
      />
    </section>
  );
}

/** Inline editácia ceny operácie — uloží on blur / Enter. */
function CenaInline({ op }: { op: Operation }): JSX.Element {
  const updateMutation = useUpdateOperacia();
  const [val, setVal] = useState(() => normalizeNum(op.cena_eur_per_mj));

  useEffect(() => {
    setVal(normalizeNum(op.cena_eur_per_mj));
  }, [op.cena_eur_per_mj]);

  async function save(): Promise<void> {
    const clean = val.replace(",", ".").trim();
    if (!clean || Number.isNaN(Number(clean))) {
      setVal(normalizeNum(op.cena_eur_per_mj));
      return;
    }
    if (Number(clean) === Number(String(op.cena_eur_per_mj))) return;
    try {
      await updateMutation.mutateAsync({
        kod: op.kod,
        data: { cena_eur_per_mj: clean },
      });
      toast({ title: "Cena uložená", description: op.label ?? op.kod });
    } catch (err) {
      setVal(normalizeNum(op.cena_eur_per_mj));
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní ceny",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        inputMode="decimal"
        aria-label={`Cena ${op.label ?? op.kod}`}
        className="h-7 w-20 text-right font-mono text-xs"
      />
      <span className="w-12 text-left font-mono text-[10px] text-muted-foreground">
        €/{op.mj ?? "MJ"}
      </span>
    </span>
  );
}

/**
 * Nastavenia → **Výrobné postupy** (KOLO 108).
 *
 * Postup je „cez akú dielňu dielec ide" — `lep_nc`, `nc_olep`, `olep`…
 * User si ich tu píše sám a v tabuľke dielcov ich už len **vyberá**,
 * preto ich tu musí vedieť aj založiť, očíslovať a povedať o každom,
 * či ide na CNC.
 *
 * **CNC príznak je prepínač, nie pravidlo v kóde.** Do KOLA 108 sa to
 * hádalo z tvaru mena (`"olep_nc" in kod` → nie je CNC), čo padlo hneď
 * pri postupe `lep_nc_olep_nc` — ten podreťazec obsahuje. Dielec by
 * stratil minimum upnutia, rotáciu aj združovanie a v cene by chýbala
 * práca. Preto je to stĺpec v tabuľke a nie skrytá heuristika.
 *
 * Platené operácie (olejovanie, striekanie) sem nepatria — tie sa
 * s cenami spravujú v Cenníku → Operácie.
 */
export function OperacieSection(): JSX.Element {
  const opQuery = useOperacie();
  const [editTarget, setEditTarget] = useState<Operation | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const items = opQuery.data?.data ?? [];
  // Postup = operácia s číslom. Predikát je **importovaný**, nie opísaný
  // (KOLO 109): v KOLE 108 tu bola vlastná kópia, takže by zúženie
  // v `lib/operacie.ts` na túto obrazovku vôbec nedosiahlo a `hw`
  // s `nc_lep_sucho` by v zozname ostali. Poradie dáva backend.
  const postupy = items.filter(jePostup);

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl">Výrobné postupy</h2>
          <p className="text-xs text-muted-foreground">
            Cez akú dielňu dielec ide. V zákazke sa postup už len{" "}
            <strong>vyberá</strong> — písať sa dá tu. Číslo je poradie
            v rozbaľovačke; postup bez čísla sa v nej ponúkne až na konci.
            Nie sú to platené operácie — tie sú v Cenníku → Operácie.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Nový postup
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[56px]">#</TableHead>
              <TableHead className="w-[170px]">Kód</TableHead>
              <TableHead className="w-[200px]">Popis</TableHead>
              <TableHead className="w-[120px]">Nadmiera</TableHead>
              <TableHead className="w-[110px]">Odčítať hrany</TableHead>
              <TableHead className="w-[92px]">Predfrez</TableHead>
              <TableHead className="w-[100px]">Ide na CNC</TableHead>
              <TableHead className="w-[60px] text-right">Akcie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {postupy.map((op) => (
              <TableRow key={op.kod} data-testid={`postup-${op.kod}`}>
                <TableCell className="py-1">
                  <CisloInline op={op} />
                </TableCell>
                <TableCell className="font-mono text-xs">{op.kod}</TableCell>
                <TableCell className="text-xs">
                  {op.label ?? op.popis ?? "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {znamienko(op.nadmiera_x_mm)} / {znamienko(op.nadmiera_y_mm)}
                </TableCell>
                <TableCell>
                  <PriznakInline
                    op={op}
                    pole="odcitat_hrany"
                    hodnota={op.odcitat_hrany ?? true}
                    popis="Hrana sa z prírezu odčíta (mínus predfrez). Pri postupoch, ktoré idú prvé na CNC a olepujú sa až potom, nechaj zapnuté s predfrezom 0 — reže sa holá doska + nadmiera (KOLO 132). Vypni pri postupoch bez olepenia, napr. nc."
                  />
                </TableCell>
                <TableCell className="py-1">
                  <PredfrezInline op={op} />
                </TableCell>
                <TableCell>
                  <PriznakInline
                    op={op}
                    pole="je_cnc"
                    hodnota={op.je_cnc ?? false}
                    popis="Platí minimum upnutia stroja, rotácia a združovanie úzkych kusov. Bez toho sa dielec reže na čistý rozmer."
                  />
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditTarget(op)}
                      className="h-7 w-7"
                      aria-label={`Upraviť ${op.kod}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PostupCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <OperaciaEditDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
      />
    </section>
  );
}

/** „+5" / „0" / „-0,5" — nula bez znamienka, inak by sa čítalo ťažko. */
function znamienko(hodnota: string | number | null | undefined): string {
  const cislo = Number(String(hodnota ?? "0").replace(",", "."));
  if (!Number.isFinite(cislo) || cislo === 0) return "0";
  return `${cislo > 0 ? "+" : ""}${normalizeNum(String(cislo))}`;
}

/**
 * Číslo postupu priamo v riadku — prečíslovanie šestice cez šesť
 * dialógov by nikto nerobil. Kolízia je 409 z backendu (stĺpec je
 * unique), takže sa vypíše a hodnota sa vráti.
 */
function CisloInline({ op }: { op: Operation }): JSX.Element {
  const updateMutation = useUpdateOperacia();
  const [val, setVal] = useState(() => String(op.postup_poradie ?? ""));

  useEffect(() => {
    setVal(String(op.postup_poradie ?? ""));
  }, [op.postup_poradie]);

  async function save(): Promise<void> {
    const clean = val.trim();
    if (clean === String(op.postup_poradie ?? "")) return;
    try {
      await updateMutation.mutateAsync({
        kod: op.kod,
        data: clean
          ? { postup_poradie: Number(clean) }
          : // Prázdno = zober číslo. Samotné `null` sa v PATCH-i od
            // „nemeniť" odlíšiť nedá, preto vlastné pole.
            { postup_poradie_clear: true },
      });
    } catch (err) {
      setVal(String(op.postup_poradie ?? ""));
      toast({
        variant: "destructive",
        title: "Číslo sa nedalo uložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ""))}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      inputMode="numeric"
      placeholder="—"
      aria-label={`Číslo postupu ${op.kod}`}
      className="h-7 w-12 text-center font-mono text-xs"
    />
  );
}

/** Checkbox priamo v riadku — celá matica postupov musí byť na očiach. */
/**
 * Predfrez postupu — prázdno znamená globálnu hodnotu z Nastavení.
 *
 * Predfrez sa robí len tam, kde dielec príde na olepovačku s **rezanou**
 * hranou z píly. Dielec, ktorý ide prvý na CNC, príde s frézovanou
 * hranou a nepredfrézuje sa — má tu **0** a hrana mu z prírezu uberie
 * celú svoju hrúbku (KOLO 115: `lep_nc` 1602×502 → 1605×505, nie
 * 1607×507 ako v TopSolide).
 *
 * `0` a prázdno sú dve **rôzne** hodnoty, preto sa nuluje cez vlastné
 * pole v PATCH-i — rovnako ako číslo postupu.
 */
function PredfrezInline({ op }: { op: Operation }): JSX.Element {
  const updateMutation = useUpdateOperacia();
  const povodne = op.predfrez_mm == null ? "" : String(op.predfrez_mm);
  const [val, setVal] = useState(povodne);

  useEffect(() => {
    setVal(op.predfrez_mm == null ? "" : String(op.predfrez_mm));
  }, [op.predfrez_mm]);

  async function save(): Promise<void> {
    const clean = val.trim().replace(",", ".");
    if (clean === povodne) return;
    try {
      await updateMutation.mutateAsync({
        kod: op.kod,
        data: clean
          ? { predfrez_mm: clean }
          : { predfrez_mm_clear: true },
      });
    } catch (err) {
      setVal(povodne);
      toast({
        variant: "destructive",
        title: "Predfrez sa nedal uložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value.replace(/[^0-9.,]/g, ""))}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      placeholder="globál"
      title="Koľko mm stiahne olepovačka z olepenej strany. Prázdne = globálna hodnota (Nastavenia → Hrany → Odpočet hrany). 0 = dielec príde z CNC s frézovanou hranou a nepredfrézuje sa, takže hrana uberie celú hrúbku."
      aria-label={`Predfrez — ${op.kod}`}
      className="h-7 w-16 px-1 text-center font-mono text-xs"
    />
  );
}

function PriznakInline({
  op,
  pole,
  hodnota,
  popis,
}: {
  op: Operation;
  pole: "odcitat_hrany" | "je_cnc";
  hodnota: boolean;
  popis: string;
}): JSX.Element {
  const updateMutation = useUpdateOperacia();

  async function prepni(nova: boolean): Promise<void> {
    try {
      await updateMutation.mutateAsync({ kod: op.kod, data: { [pole]: nova } });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <input
      type="checkbox"
      checked={hodnota}
      onChange={(e) => void prepni(e.target.checked)}
      title={popis}
      aria-label={`${pole === "je_cnc" ? "Ide na CNC" : "Odčítať hrany"} — ${op.kod}`}
      className="h-3.5 w-3.5 cursor-pointer accent-primary"
    />
  );
}

/**
 * Nový výrobný postup. Nemá kategóriu, MJ ani cenu — to sú polia
 * platenej operácie a tu by len mýlili.
 */
function PostupCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const createMutation = useCreateOperacia();
  const [kod, setKod] = useState("");
  const [label, setLabel] = useState("");
  const [cislo, setCislo] = useState("");
  const [jeCnc, setJeCnc] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setKod("");
      setLabel("");
      setCislo("");
      setJeCnc(true);
    }
  }, [open]);

  async function handle(): Promise<void> {
    setSubmitting(true);
    try {
      await createMutation.mutateAsync({
        kod,
        label: label || null,
        popis: null,
        kategoria: null,
        mj: null,
        cena_eur_per_mj: "0",
        rezia_pct: "0",
        je_cnc: jeCnc,
        postup_poradie: cislo ? Number(cislo) : null,
      });
      toast({ title: "Postup pridaný", description: kod });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nový výrobný postup</DialogTitle>
          <DialogDescription className="sr-only">
            Výrobný postup je sled dielenských krokov — kód, číslo
            v rozbaľovačke a či dielec ide na CNC.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="postup-kod">Kód *</Label>
            <Input
              id="postup-kod"
              autoFocus
              placeholder="napr. lep_nc_olep_nc"
              value={kod}
              onChange={(e) => setKod(e.target.value.toLowerCase())}
              pattern="^[a-z][a-z0-9_]*$"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Presne tak, ako operáciu pomenúvaš v TopSolide — import ju
              podľa tohto kódu nájde.
            </p>
          </div>
          <div>
            <Label htmlFor="postup-label">Popis</Label>
            <Input
              id="postup-label"
              placeholder="napr. Lepenie → CNC → olepenie → CNC"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="postup-cislo">Číslo</Label>
            <Input
              id="postup-cislo"
              inputMode="numeric"
              placeholder="napr. 6"
              value={cislo}
              onChange={(e) => setCislo(e.target.value.replace(/[^0-9]/g, ""))}
              className="w-24"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Poradie v rozbaľovačke. Bez čísla sa postup ponúkne na konci.
            </p>
          </div>
          <label className="flex cursor-pointer items-start gap-2 text-xs">
            <input
              type="checkbox"
              checked={jeCnc}
              onChange={(e) => setJeCnc(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-primary"
            />
            <span>
              Ide na CNC
              <span className="block text-[11px] text-muted-foreground">
                Platí minimum upnutia stroja, rotácia a združovanie úzkych
                kusov. Nadmieru a odčítanie hrán nastavíš po pridaní
                ceruzkou v riadku.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zrušiť
          </Button>
          <Button disabled={!kod || submitting} onClick={handle}>
            {submitting ? "Pridáva sa…" : "Pridať"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Select kategórie platenej operácie (create aj edit dialóg). */
function KategoriaSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <Label htmlFor="op-kategoria">Kategória</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="op-kategoria" aria-label="Kategória operácie">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— nezaradená —</SelectItem>
          {KATEGORIE_OPERACII.map((k) => (
            <SelectItem key={k.kod} value={k.kod}>
              {k.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** MJ + cena + réžia polia operácie (create aj edit dialóg). */
function OperaciaCenaFields({
  mj,
  cena,
  rezia,
  onMj,
  onCena,
  onRezia,
}: {
  mj: string;
  cena: string;
  rezia: string;
  onMj: (v: string) => void;
  onCena: (v: string) => void;
  onRezia: (v: string) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <Label htmlFor="op-mj">MJ ceny</Label>
        <Select value={mj} onValueChange={onMj}>
          <SelectTrigger id="op-mj" aria-label="MJ operácie">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— bez ceny —</SelectItem>
            <SelectItem value="m2">m² (plocha)</SelectItem>
            <SelectItem value="m">m (dĺžka X)</SelectItem>
            <SelectItem value="ks">ks</SelectItem>
            <SelectItem value="hod">hod (1/ks)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="op-cena">Cena €/MJ</Label>
        <Input
          id="op-cena"
          inputMode="decimal"
          placeholder="0"
          value={cena}
          onChange={(e) => onCena(e.target.value)}
          disabled={mj === "none"}
        />
      </div>
      <div>
        <Label htmlFor="op-rezia">Réžia %</Label>
        <Input
          id="op-rezia"
          inputMode="decimal"
          placeholder="0"
          value={rezia}
          onChange={(e) => onRezia(e.target.value)}
          disabled={mj === "none"}
        />
      </div>
    </div>
  );
}

function OperaciaCreateDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSubmit: (data: {
    kod: string;
    label: string | null;
    popis: string | null;
    kategoria: string | null;
    mj?: string | null;
    cena_eur_per_mj: string;
    rezia_pct: string;
    je_cnc: boolean;
  }) => Promise<void>;
}): JSX.Element {
  const [kod, setKod] = useState("");
  const [label, setLabel] = useState("");
  const [popis, setPopis] = useState("");
  const [kategoria, setKategoria] = useState<string>("none");
  const [mj, setMj] = useState<string>("none");
  const [cena, setCena] = useState("");
  const [rezia, setRezia] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setKod("");
      setLabel("");
      setPopis("");
      setKategoria("none");
      setMj("none");
      setCena("");
      setRezia("");
    }
  }, [open]);

  async function handle(): Promise<void> {
    setSubmitting(true);
    try {
      await onSubmit({
        kod,
        label: label || null,
        popis: popis || null,
        kategoria: kategoria !== "none" ? kategoria : null,
        mj: mj !== "none" ? mj : null,
        cena_eur_per_mj: cena || "0",
        rezia_pct: rezia || "0",
        // Platená operácia (olejovanie, striekanie) nie je výrobný
        // postup — na CNC dielec neposiela. Prepína sa to pri postupoch
        // v Nastaveniach, nie tu.
        je_cnc: false,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nová operácia</DialogTitle>
          <DialogDescription className="sr-only">
            Operácia je práca účtovaná k dielcu — kód, merná jednotka a cena.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="op-kod">Kód *</Label>
            <Input
              id="op-kod"
              autoFocus
              placeholder="napr. olejovanie (lowercase, žiadne medzery)"
              value={kod}
              onChange={(e) => setKod(e.target.value.toLowerCase())}
              pattern="^[a-z][a-z0-9_]*$"
            />
          </div>
          <div>
            <Label htmlFor="op-label">Label</Label>
            <Input
              id="op-label"
              placeholder="napr. Olejovanie povrchu"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="op-popis">Popis</Label>
            <Input
              id="op-popis"
              placeholder="Voliteľný popis"
              value={popis}
              onChange={(e) => setPopis(e.target.value)}
            />
          </div>
          <KategoriaSelect value={kategoria} onChange={setKategoria} />
          <OperaciaCenaFields
            mj={mj}
            cena={cena}
            rezia={rezia}
            onMj={setMj}
            onCena={setCena}
            onRezia={setRezia}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zrušiť
          </Button>
          <Button disabled={!kod || submitting} onClick={handle}>
            {submitting ? "Pridáva sa…" : "Pridať"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OperaciaEditDialog({
  target,
  onClose,
}: {
  target: Operation | null;
  onClose: () => void;
}): JSX.Element {
  const updateMutation = useUpdateOperacia();
  const [label, setLabel] = useState("");
  const [popis, setPopis] = useState("");
  const [kategoria, setKategoria] = useState<string>("none");
  const [mj, setMj] = useState<string>("none");
  const [cena, setCena] = useState("");
  const [rezia, setRezia] = useState("");
  // Rozmerová nadmiera (KOLO 104) — má ju aj systémové triedenie z píly,
  // preto je mimo vetvy s cenou. `lep_nc` ide prvý na CNC a olepuje sa až
  // potom, takže sa reže väčší; ostatné operácie majú 0.
  const [nadX, setNadX] = useState("");
  const [nadY, setNadY] = useState("");
  // Odčítava sa hrana z prírezu na predfrez? (KOLO 105) Od KOLA 132 áno
  // aj pri postupoch, ktoré idú prvé na CNC (s predfrezom 0 — reže sa holá
  // doska + nadmiera); vypnuté len pri postupoch bez olepenia (`nc`).
  const [odcitat, setOdcitat] = useState(true);

  useEffect(() => {
    if (target) {
      setLabel(target.label ?? "");
      setPopis(target.popis ?? "");
      setKategoria(target.kategoria ?? "none");
      setMj(target.mj ?? "none");
      setCena(normalizeNum(target.cena_eur_per_mj));
      setRezia(normalizeNum(target.rezia_pct));
      setNadX(normalizeNum(target.nadmiera_x_mm ?? "0"));
      setNadY(normalizeNum(target.nadmiera_y_mm ?? "0"));
      setOdcitat(target.odcitat_hrany ?? true);
    }
  }, [target]);

  async function save(): Promise<void> {
    if (!target) return;
    try {
      await updateMutation.mutateAsync({
        kod: target.kod,
        data: target.je_systemova
          ? // triedenie z píly — má len label/popis a rozmerovú nadmieru
            {
              label: label || null,
              popis: popis || null,
              nadmiera_x_mm: nadX || "0",
              nadmiera_y_mm: nadY || "0",
              odcitat_hrany: odcitat,
            }
          : {
              label: label || null,
              popis: popis || null,
              kategoria: kategoria !== "none" ? kategoria : null,
              mj: mj !== "none" ? mj : null,
              cena_eur_per_mj: cena || "0",
              rezia_pct: rezia || "0",
              nadmiera_x_mm: nadX || "0",
              nadmiera_y_mm: nadY || "0",
              odcitat_hrany: odcitat,
            },
      });
      toast({ title: "Operácia uložená" });
      onClose();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upraviť operáciu — {target?.kod}</DialogTitle>
          <DialogDescription className="sr-only">
            Úprava kódu, mernej jednotky a ceny operácie.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ope-label">Label</Label>
            <Input
              id="ope-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ope-popis">Popis</Label>
            <Input
              id="ope-popis"
              value={popis}
              onChange={(e) => setPopis(e.target.value)}
            />
          </div>
          <div className="rounded border bg-background-alt p-2">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Rozmerová nadmiera
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="ope-nad-x">X (mm)</Label>
                <Input
                  id="ope-nad-x"
                  type="number"
                  min={0}
                  step="0.5"
                  value={nadX}
                  onChange={(e) => setNadX(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="ope-nad-y">Y (mm)</Label>
                <Input
                  id="ope-nad-y"
                  type="number"
                  min={0}
                  step="0.5"
                  value={nadY}
                  onChange={(e) => setNadY(e.target.value)}
                />
              </div>
            </div>
            <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={odcitat}
                onChange={(e) => setOdcitat(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-primary"
              />
              <span>
                Odčítať hrany (dielec sa olepuje pred rezom)
                <span className="block text-[11px] text-muted-foreground">
                  Zapnuté: hrana sa z prírezu odráta o predfrez menej — 1 mm
                  hrana uberie 0,5 mm. Vypni pri operáciách, ktoré idú prvé
                  na CNC a olepujú sa až potom (`nc`, `lep_nc`) — tam sa reže
                  presne základ vyššie.
                </span>
              </span>
            </label>
            <p className="mt-1 text-[11px] text-muted-foreground">
              O koľko sa dielec s touto operáciou reže väčší.
            </p>
          </div>
          {!target?.je_systemova ? (
            <>
              <KategoriaSelect value={kategoria} onChange={setKategoria} />
              <OperaciaCenaFields
                mj={mj}
                cena={cena}
                rezia={rezia}
                onMj={setMj}
                onCena={setCena}
                onRezia={setRezia}
              />
            </>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Triedenie z píly — cenu ani kategóriu nemá.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Zrušiť
          </Button>
          <Button onClick={save} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Ukladá sa…" : "Uložiť"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
